import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';

const PatentCard = ({
  patent,
  user,
  savedPatents,
  handleSavePatent,
  savingPatent,
  citationCounts,
  expandedCitations,
  handleExpandCitations,
  citationDetails,
  citationDetailsLoading,
  handleTieredAnalysis,
  analysisLoading,
  userUsage,
  handleExpandFamily,
  expandedFamily,
  familyLoading,
  familyData,
  setLoginPromptMessage,
  setShowLoginPrompt,
  addToCompare,
  compareList,
  openShareModal,
  patentSummaries,
  setPatentSummaries,
  // NEW PROPS FOR SEARCH PRO
  showEnhancedButtons = false,
  onViewClaims,
  onViewSummary,
  onViewPCT,
  onViewDescription
}) => {
  // Local state for enhanced content sections
  const [expandedClaims, setExpandedClaims] = useState(false);
  const [expandedSummary, setExpandedSummary] = useState(false);
  const [expandedPCT, setExpandedPCT] = useState(false);
  const [expandedDesc, setExpandedDesc] = useState(false);
  
  const [claimsData, setClaimsData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [pctData, setPCTData] = useState(null);
  const [descData, setDescData] = useState(null);
  
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingPCT, setLoadingPCT] = useState(false);
  const [loadingDesc, setLoadingDesc] = useState(false);

  const handleViewClaims = async () => {
    if (!expandedClaims && !claimsData && onViewClaims) {
      setLoadingClaims(true);
      const data = await onViewClaims(patent.id, patent.status);
      setClaimsData(data);
      setLoadingClaims(false);
    }
    setExpandedClaims(!expandedClaims);
  };

  const handleViewSummary = async () => {
    if (!expandedSummary && !summaryData && onViewSummary) {
      setLoadingSummary(true);
      const data = await onViewSummary(patent.id, patent.status);
      setSummaryData(data);
      setLoadingSummary(false);
    }
    setExpandedSummary(!expandedSummary);
  };

  const handleViewPCT = async () => {
    if (!expandedPCT && !pctData && onViewPCT) {
      setLoadingPCT(true);
      const data = await onViewPCT(patent.id, patent.status);
      setPCTData(data);
      setLoadingPCT(false);
    }
    setExpandedPCT(!expandedPCT);
  };

  const handleViewDescription = async () => {
    if (!expandedDesc && !descData && onViewDescription) {
      setLoadingDesc(true);
      const data = await onViewDescription(patent.id, patent.status);
      setDescData(data);
      setLoadingDesc(false);
    }
    setExpandedDesc(!expandedDesc);
  };

  return (
    <div
      key={patent.id}
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        background: 'white',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}
    >
      {/* Card Header - Status Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 20px',
        background: ['GRANTED', 'LIVE'].includes(patent.status?.toUpperCase())
          ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
          : patent.status?.toUpperCase() === 'PENDING'
          ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
          : 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
        borderBottom: ['GRANTED', 'LIVE'].includes(patent.status?.toUpperCase())
          ? '2px solid #10b981'
          : patent.status?.toUpperCase() === 'PENDING'
          ? '2px solid #f59e0b'
          : '2px solid #ef4444'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <a 
            href={`https://patents.google.com/patent/US${patent.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#1e40af',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            US{patent.id}
            <span style={{ fontSize: '12px', opacity: 0.7 }}>↗</span>
          </a>
          
          <span style={{
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            background: ['GRANTED', 'LIVE'].includes(patent.status?.toUpperCase()) ? '#10b981' : patent.status?.toUpperCase() === 'PENDING' ? '#f59e0b' : '#ef4444',
            color: 'white'
          }}>
            {['GRANTED', 'LIVE'].includes(patent.status?.toUpperCase()) ? '● GRANTED' : patent.status?.toUpperCase() === 'PENDING' ? '○ PENDING' : '○ EXPIRED'}
          </span>
          
          {/* Cached Badge */}
          {patent.cacheStatus && (patent.cacheStatus.quick || patent.cacheStatus.standard || patent.cacheStatus.deep) && (
            <span style={{
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '600',
              background: '#d1fae5',
              color: '#065f46'
            }}>
              ✓ Cached
            </span>
          )}
          
          {/* Citation strength badge */}
          {citationCounts[patent.id]?.forward_citations >= 20 && (
            <span style={{
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '600',
              background: citationCounts[patent.id]?.forward_citations >= 50 ? '#ede9fe' : '#dbeafe',
              color: citationCounts[patent.id]?.forward_citations >= 50 ? '#7c3aed' : '#2563eb'
            }}>
              ⭐ {citationCounts[patent.id]?.forward_citations >= 50 ? 'Highly Cited' : 'Well Cited'}
            </span>
          )}
        </div>
        
        {/* Citation Counts in Header */}
        {citationCounts[patent.id] && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: '700', fontSize: '18px', color: '#7c3aed' }}>
                {citationCounts[patent.id].forward_citations}
              </div>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b' }}>
                Citations
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#64748b' }}>
                {citationCounts[patent.id].backward_citations}
              </div>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8' }}>
                Refs
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Card Body */}
      <div style={{ padding: '20px' }}>
        <h3 style={{
          fontSize: '17px',
          fontWeight: '600',
          color: '#1e293b',
          lineHeight: '1.4',
          marginBottom: '12px'
        }}>
          {patent.title}
        </h3>
        
        <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.65', marginBottom: '14px' }}>
          {patent.summary}
        </p>
        
        <div style={{ marginTop: '8px' }}>
          <span style={{ fontSize: '14px', color: '#545f6b' }}>
            Status: {['GRANTED', 'LIVE'].includes(patent.status?.toUpperCase()) ? 'Live' : patent.status?.toUpperCase() === 'PENDING' ? 'Pending' : patent.status}{patent.filing_date ? ` | Filed: ${patent.filing_date}` : ''} | {patent.status?.toUpperCase() === 'PENDING' ? 'Published' : 'Granted'}: {patent.grant_date} | Assignee: {patent.assignee}{patent.ipc_codes?.length > 0 ? ` | IPC: ${patent.ipc_codes.join(', ')}` : ''}
          </span>
          {citationCounts[patent.id] && (
            <div style={{ marginTop: '6px' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button 
                  onClick={() => handleExpandCitations(patent.id)}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    padding: 0, 
                    cursor: 'pointer',
                    fontSize: '13px', 
                    color: '#6b7280',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <span style={{ textDecoration: 'none', marginRight: '4px' }}>{expandedCitations === patent.id ? '▼' : '▶'}</span>
                  Cited by: <strong style={{ color: citationCounts[patent.id].forward_citations > 10 ? '#10b981' : '#374151' }}>{citationCounts[patent.id].forward_citations}</strong>
                  <span style={{ margin: '0 4px' }}>|</span>
                  Cites: <strong style={{ color: '#374151' }}>{citationCounts[patent.id].backward_citations}</strong>
                </button>
                {citationCounts[patent.id].forward_citations >= 50 && (
                  <span style={{ background: '#fbbf24', color: '#78350f', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>
                    Highly Cited
                  </span>
                )}
                {citationCounts[patent.id].forward_citations >= 100 && (
                  <span style={{ background: '#f59e0b', color: '#78350f', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', marginLeft: '-8px' }}>
                    Landmark
                  </span>
                )}
              </div>
              
              {expandedCitations === patent.id && (
                <div style={{ marginTop: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  {citationDetailsLoading ? (
                    <div style={{ textAlign: 'center', color: '#64748b' }}>Loading citations...</div>
                  ) : (
                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                      {/* Forward Citations */}
                      <div style={{ flex: '1', minWidth: '280px' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#10b981', marginBottom: '8px' }}>
                          Cited By ({citationCounts[patent.id].forward_citations})
                        </h4>
                        {citationDetails.forward.length === 0 ? (
                          <div style={{ fontSize: '13px', color: '#94a3b8' }}>No forward citations</div>
                        ) : (
                          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {citationDetails.forward.map((cite, idx) => (
                              <div key={idx} style={{ fontSize: '12px', marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #e2e8f0' }}>
                                <a 
                                  href={`https://worldwide.espacenet.com/patent/search?q=pn%3D${cite.patent_number}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#3b82f6', fontWeight: '500' }}
                                >
                                  {cite.patent_number}
                                </a>
                                <div style={{ color: '#64748b', fontSize: '11px' }}>{cite.title}</div>
                                <div style={{ color: '#94a3b8', fontSize: '10px' }}>{cite.assignee || 'Unknown assignee'}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Backward Citations */}
                      <div style={{ flex: '1', minWidth: '280px' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#6366f1', marginBottom: '8px' }}>
                          Cites ({citationCounts[patent.id].backward_citations})
                        </h4>
                        {citationDetails.backward.length === 0 ? (
                          <div style={{ fontSize: '13px', color: '#94a3b8' }}>No backward citations</div>
                        ) : (
                          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {citationDetails.backward.map((cite, idx) => (
                              <div key={idx} style={{ fontSize: '12px', marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #e2e8f0' }}>
                                <a 
                                  href={`https://worldwide.espacenet.com/patent/search?q=pn%3D${cite.patent_number}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#3b82f6', fontWeight: '500' }}
                                >
                                  {cite.patent_number}
                                </a>
                                <div style={{ color: '#64748b', fontSize: '11px' }}>{cite.title}</div>
                                <div style={{ color: '#94a3b8', fontSize: '10px' }}>{cite.assignee || 'Unknown assignee'}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        

        {/* Patent Family Section */}
        <div style={{ marginTop: '8px' }}>
          <button
            onClick={() => handleExpandFamily(patent.id)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontSize: '13px',
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <span style={{ marginRight: '4px' }}>{expandedFamily === patent.id ? '▼' : '▶'}</span>
            🌍 Patent Family
          </button>

          {expandedFamily === patent.id && (
            <div style={{ marginTop: '12px', padding: '12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
              {familyLoading ? (
                <div style={{ textAlign: 'center', color: '#64748b' }}>Loading family data...</div>
              ) : (
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                  {/* Foreign Priorities */}
                  <div style={{ flex: '1', minWidth: '200px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#15803d', marginBottom: '8px' }}>
                      Foreign Priorities ({familyData.foreign_priorities.length})
                    </h4>
                    {familyData.foreign_priorities.length === 0 ? (
                      <div style={{ fontSize: '13px', color: '#94a3b8' }}>No foreign priorities found</div>
                    ) : (
                      <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        {familyData.foreign_priorities.map((priority, idx) => (
                          <div key={idx} style={{ fontSize: '12px', marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #dcfce7' }}>
                            <span style={{ fontWeight: '600', color: '#166534' }}>{priority.country}</span>
                            <span style={{ marginLeft: '8px', color: '#64748b' }}>{priority.application_id}</span>
                            {priority.filing_date && (
                              <div style={{ fontSize: '11px', color: '#94a3b8' }}>Filed: {priority.filing_date}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Family Members */}
                  <div style={{ flex: '1', minWidth: '200px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#0369a1', marginBottom: '8px' }}>
                      US Family Members ({familyData.family_members.length})
                    </h4>
                    {familyData.family_members.length === 0 ? (
                      <div style={{ fontSize: '13px', color: '#94a3b8' }}>No other US patents in family</div>
                    ) : (
                      <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        {familyData.family_members.map((member, idx) => (
                          <div key={idx} style={{ fontSize: '12px', marginBottom: '4px' }}>
                            <a         
                              href={`https://worldwide.espacenet.com/patent/search?q=pn%3D${member}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#0284c7', fontWeight: '500' }}
                            >
                              {member}
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* EXISTING BUTTON ROW */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        
          <button
            onClick={() => handleTieredAnalysis(patent.id, 'quick', patent.title, patent.summary, patent.status || 'Unknown')}
            disabled={analysisLoading[`${patent.id}-quick`] || (userUsage && userUsage.quick_analyses.remaining <= 0)}
            title={userUsage ? `Brief overview (${userUsage.quick_analyses.remaining} of ${userUsage.quick_analyses.limit} remaining today)` : "Brief 2-3 sentence overview"}
            style={{
              background: analysisLoading[`${patent.id}-quick`] ? '#94a3b8' : (userUsage && userUsage.quick_analyses.remaining <= 0) ? '#94a3b8' : '#10b981',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              cursor: (analysisLoading[`${patent.id}-quick`] || (userUsage && userUsage.quick_analyses.remaining <= 0)) ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {analysisLoading[`${patent.id}-quick`] ? (
              <><span style={{ display: 'inline-block', width: '12px', height: '12px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span> Quick...</>
            ) : (
              <>Quick {userUsage && (userUsage.quick_analyses.limit >= 999 ? <span style={{ fontSize: '10px', opacity: 0.9 }}>(Unlimited)</span> : <span style={{ fontSize: '10px', opacity: 0.9 }}>({userUsage.quick_analyses.remaining} left)</span>)}</>
            )}
          </button>
          
          <button
            onClick={() => userUsage && userUsage.standard_analyses.limit === 0 ? (() => { setLoginPromptMessage('Standard Analysis requires Premium. Upgrade to unlock deeper patent analysis with claim mapping.'); setShowLoginPrompt(true); })() : handleTieredAnalysis(patent.id, 'standard', patent.title, patent.summary, patent.status || 'Unknown')}
            disabled={analysisLoading[`${patent.id}-standard`] || (userUsage && userUsage.standard_analyses.limit > 0 && userUsage.standard_analyses.remaining <= 0)}
            title={userUsage && userUsage.standard_analyses.limit === 0 ? "Upgrade to Premium for Standard Analysis" : userUsage ? `Detailed analysis (${userUsage.standard_analyses.remaining} of ${userUsage.standard_analyses.limit} remaining today)` : "Detailed analysis"}
            style={{
              background: analysisLoading[`${patent.id}-standard`] ? '#94a3b8' : (userUsage && userUsage.standard_analyses.limit === 0) ? '#64748b' : (userUsage && userUsage.standard_analyses.remaining <= 0) ? '#94a3b8' : '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              cursor: (analysisLoading[`${patent.id}-standard`] || (userUsage && userUsage.standard_analyses.limit > 0 && userUsage.standard_analyses.remaining <= 0)) ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {analysisLoading[`${patent.id}-standard`] ? (
              <><span style={{ display: 'inline-block', width: '12px', height: '12px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span> Analyzing...</>
            ) : (
              <>{userUsage && userUsage.standard_analyses.limit === 0 ? '🔒 ' : ''}Standard {userUsage && userUsage.standard_analyses.limit > 0 && (userUsage.standard_analyses.limit >= 999 ? <span style={{ fontSize: '10px', opacity: 0.9 }}>(Unlimited)</span> : <span style={{ fontSize: '10px', opacity: 0.9 }}>({userUsage.standard_analyses.remaining} left)</span>)}</>
            )}
          </button>
          
          <button
            onClick={() => userUsage && userUsage.deep_analyses.limit === 0 ? (() => { setLoginPromptMessage('Deep Analysis requires Pro. Upgrade to unlock comprehensive technical, commercial and legal insights.'); setShowLoginPrompt(true); })() : handleTieredAnalysis(patent.id, 'deep', patent.title, patent.summary, patent.status || 'Unknown')}
            disabled={analysisLoading[`${patent.id}-deep`] || (userUsage && userUsage.deep_analyses.limit > 0 && userUsage.deep_analyses.remaining <= 0)}
            title={userUsage && userUsage.deep_analyses.limit === 0 ? "Upgrade to Pro for Deep Analysis" : userUsage ? `Full analysis (${userUsage.deep_analyses.remaining} of ${userUsage.deep_analyses.limit} remaining today)` : "Full technical analysis"}
            style={{
              background: analysisLoading[`${patent.id}-deep`] ? '#94a3b8' : (userUsage && userUsage.deep_analyses.limit === 0) ? '#64748b' : (userUsage && userUsage.deep_analyses.remaining <= 0) ? '#94a3b8' : '#8b5cf6',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              cursor: (analysisLoading[`${patent.id}-deep`] || (userUsage && userUsage.deep_analyses.limit > 0 && userUsage.deep_analyses.remaining <= 0)) ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {analysisLoading[`${patent.id}-deep`] ? (
              <><span style={{ display: 'inline-block', width: '12px', height: '12px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span> Deep...</>
            ) : (
              <>{userUsage && userUsage.deep_analyses.limit === 0 ? '🔒 ' : ''}Deep {userUsage && userUsage.deep_analyses.limit > 0 && (userUsage.deep_analyses.limit >= 999 ? <span style={{ fontSize: '10px', opacity: 0.9 }}>(Unlimited)</span> : <span style={{ fontSize: '10px', opacity: 0.9 }}>({userUsage.deep_analyses.remaining} left)</span>)}</>
            )}
          </button>

          <button 
            onClick={() => handleSavePatent(patent)} 
            disabled={savingPatent === patent.id}
            style={{
              background: savedPatents.has(patent.id) ? '#10b981' : '#53dafc',
              color: 'white', 
              border: 'none',
              padding: '8px 16px', 
              borderRadius: '4px', 
              cursor: savingPatent === patent.id ? 'not-allowed' : 'pointer',
              fontSize: '14px', 
              fontWeight: '600', 
              opacity: savingPatent === patent.id ? 0.6 : 1,
              transition: 'all 0.2s'
            }}
          >
            {savingPatent === patent.id ? 'Saving...' : 
             savedPatents.has(patent.id) ? '✓ Saved' : 'Save Patent'}
          </button>
          
          <button
            onClick={() => addToCompare(patent)}
            disabled={compareList.find(p => p.id === patent.id)}
            style={{
              background: compareList.find(p => p.id === patent.id) ? '#10b981' : '#8b5cf6',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: compareList.find(p => p.id === patent.id) ? 'default' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: compareList.find(p => p.id === patent.id) ? 0.7 : 1,
              transition: 'all 0.2s'
            }}
          >
            {compareList.find(p => p.id === patent.id) ? '✓ In Compare' : 'Compare'}
          </button>
          
          {user && (user.tier === 'premium' || user.tier === 'pro') && (
            <button
              onClick={() => openShareModal(patent)}
              style={{
                background: '#059669',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
            >
              Share
            </button>
          )}
        </div>

        {/* NEW ENHANCED BUTTONS ROW - ONLY SHOWS IN SEARCH PRO */}
        {showEnhancedButtons && (
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={handleViewClaims}
              style={{
                background: '#f97316',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
            >
              {loadingClaims ? 'Loading...' : 'View Claims'}
            </button>
            <button
              onClick={handleViewSummary}
              style={{
                background: '#0891b2',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
            >
              {loadingSummary ? 'Loading...' : 'View Summary'}
            </button>
            <button
              onClick={handleViewPCT}
              style={{
                background: '#7c3aed',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
            >
              {loadingPCT ? 'Loading...' : 'View PCT'}
            </button>
            <button
              onClick={handleViewDescription}
              style={{
                background: '#059669',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
            >
              {loadingDesc ? 'Loading...' : 'View Description'}
            </button>
          </div>
        )}

        {/* EXPANDABLE CONTENT SECTIONS */}
        {expandedClaims && claimsData && (
          <div style={{ marginTop: '12px', padding: '16px', backgroundColor: '#fff7ed', borderLeft: '4px solid #f97316', borderRadius: '4px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#c2410c', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
              <span>Claims:</span>
              <button onClick={() => setExpandedClaims(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#64748b' }}>✕</button>
            </h4>
            <div style={{ fontSize: '13px', color: '#78350f', lineHeight: '1.6', maxHeight: '400px', overflowY: 'auto' }}>
              {claimsData.claims_text || claimsData.claims?.map((c, i) => (
                <div key={i} style={{ marginBottom: '12px' }}>
                  <strong>Claim {c.claim_number}:</strong> {c.text}
                </div>
              )) || 'No claims data available'}
            </div>
          </div>
        )}

        {expandedSummary && summaryData && (
          <div style={{ marginTop: '12px', padding: '16px', backgroundColor: '#ecfeff', borderLeft: '4px solid #0891b2', borderRadius: '4px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#0e7490', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
              <span>Summary:</span>
              <button onClick={() => setExpandedSummary(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#64748b' }}>✕</button>
            </h4>
            <div style={{ fontSize: '13px', color: '#164e63', lineHeight: '1.6' }}>
              {summaryData.summary_text || 'No summary available'}
            </div>
          </div>
        )}

        {expandedPCT && pctData && (
          <div style={{ marginTop: '12px', padding: '16px', backgroundColor: '#f5f3ff', borderLeft: '4px solid #7c3aed', borderRadius: '4px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#6d28d9', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
              <span>PCT Data:</span>
              <button onClick={() => setExpandedPCT(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#64748b' }}>✕</button>
            </h4>
            <div style={{ fontSize: '13px', color: '#5b21b6', lineHeight: '1.6' }}>
              {pctData.has_pct ? (
                <>
                  {pctData.filed_country && <div><strong>Country:</strong> {pctData.filed_country}</div>}
                  {pctData.pct_doc_number && <div><strong>PCT Number:</strong> {pctData.pct_doc_number}</div>}
                  {pctData.priority_date && <div><strong>Priority Date:</strong> {pctData.priority_date}</div>}
                </>
              ) : 'No PCT data available'}
            </div>
          </div>
        )}

        {expandedDesc && descData && (
          <div style={{ marginTop: '12px', padding: '16px', backgroundColor: '#f0fdf4', borderLeft: '4px solid #059669', borderRadius: '4px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#047857', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
              <span>Description:</span>
              <button onClick={() => setExpandedDesc(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#64748b' }}>✕</button>
            </h4>
            <div style={{ fontSize: '13px', color: '#065f46', lineHeight: '1.6', maxHeight: '400px', overflowY: 'auto' }}>
              {descData.description_text || 'No description available'}
            </div>
          </div>
        )}

        {/* Show summary inline when visible */}
        {patentSummaries[patent.id]?.visible && (
          <div style={{
            marginTop: '12px',
            padding: '16px',
            backgroundColor: '#f5f9fc',
            borderLeft: '4px solid #53dafc',
            borderRadius: '4px'
          }}>
            <h4 style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#1d2934', 
              marginBottom: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>{patentSummaries[patent.id]?.tierName || 'AI'} Analysis:</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {patentSummaries[patent.id]?.fromCache && (
                  <span style={{ 
                    fontSize: '11px', 
                    color: '#065f46', 
                    fontWeight: '600',
                    background: '#d1fae5',
                    padding: '2px 6px',
                    borderRadius: '4px'
                  }}>✓ Cached</span>
                )}
                <button
                  onClick={() => setPatentSummaries(prev => ({
                    ...prev,
                    [patent.id]: { ...prev[patent.id], visible: false }
                  }))}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#64748b',
                    padding: '0 4px'
                  }}
                  title="Hide analysis"
                >✕</button>
              </div>
            </h4>
  
            <div style={{ 
              fontSize: '14px', 
              color: '#474f58', 
              lineHeight: '1.6',
              margin: 0
            }}>
              <ReactMarkdown
                components={{
                  ul: ({node, ...props}) => <ul style={{ marginLeft: '20px', paddingLeft: '0' }} {...props} />,
                  li: ({node, ...props}) => <li style={{ marginBottom: '4px' }} {...props} />
                }}
              >
                {patentSummaries[patent.id].text}
              </ReactMarkdown>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatentCard;